/**
 * API client Textprint (Phase 2 — Imprimerie + Textile).
 * Endpoints /v1/operations/textprint/*
 */
import { apiClient, unwrap, unwrapPaginated } from './client';
import type {
  CommandeImpression, ConsommableImpression, LigneCommande,
  MachineImpression, OrdreImpression,
  PrioriteOF, StatutCommandeImpression, StatutMachineImpression, StatutOFImpression,
  TypeConsommable, TypeMachine, UniteConsommable,
} from '@/types';
import type { Paginated } from './operations';

// ── Machines ───────────────────────────────────────────────────────────────

export interface MachineListParams {
  statut?:      StatutMachineImpression;
  typeMachine?: TypeMachine;
  search?:      string;
  page?:        number;
  limit?:       number;
}

export interface CreateMachinePayload {
  code:                 string;
  nom:                  string;
  typeMachine:          TypeMachine;
  modele?:              string;
  numeroSerie?:         string;
  fournisseur?:         string;
  dateAcquisition?:     string;
  prixAchatCents?:      number;
  compteurInitial?:     number;
  compteurActuel?:      number;
  statut?:              StatutMachineImpression;
  garantieExpire?:      string;
  contratSav?:          boolean;
  contactSav?:          string;
  derniereMaintenance?: string;
  notes?:               string;
}
export type UpdateMachinePayload = Partial<CreateMachinePayload>;

export const textprintMachinesApi = {
  list: async (params: MachineListParams = {}): Promise<Paginated<MachineImpression>> => {
    const r = await apiClient.get('/operations/textprint/machines', { params });
    return unwrapPaginated<MachineImpression>(r);
  },
  get: async (id: string): Promise<MachineImpression> => {
    const r = await apiClient.get(`/operations/textprint/machines/${id}`);
    return unwrap<MachineImpression>(r);
  },
  create: async (payload: CreateMachinePayload): Promise<MachineImpression> => {
    const r = await apiClient.post('/operations/textprint/machines', payload);
    return unwrap<MachineImpression>(r);
  },
  update: async (id: string, payload: UpdateMachinePayload): Promise<MachineImpression> => {
    const r = await apiClient.patch(`/operations/textprint/machines/${id}`, payload);
    return unwrap<MachineImpression>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/machines/${id}`);
  },
};

// ── Commandes ──────────────────────────────────────────────────────────────

export interface CommandeListParams {
  statut?:   StatutCommandeImpression;
  clientId?: string;
  search?:   string;
  page?:     number;
  limit?:    number;
}

export interface CreateCommandePayload {
  reference?:           string;
  clientId?:            string;
  dateLivraisonPrevue?: string;
  lignes:               LigneCommande[];
  totalHtCents:         number;
  tvaCents?:            number;
  totalTtcCents:        number;
  acompteCents?:        number;
  statut?:              StatutCommandeImpression;
  adresseLivraison?:    string;
  instructions?:        string;
  notes?:               string;
}
export interface UpdateCommandePayload {
  clientId?:            string;
  dateLivraisonPrevue?: string;
  dateLivraisonReelle?: string;
  lignes?:              LigneCommande[];
  totalHtCents?:        number;
  tvaCents?:            number;
  totalTtcCents?:       number;
  acompteCents?:        number;
  statut?:              StatutCommandeImpression;
  adresseLivraison?:    string;
  instructions?:        string;
  notes?:               string;
}

export const textprintCommandesApi = {
  list: async (params: CommandeListParams = {}): Promise<Paginated<CommandeImpression>> => {
    const r = await apiClient.get('/operations/textprint/commandes', { params });
    return unwrapPaginated<CommandeImpression>(r);
  },
  get: async (id: string): Promise<CommandeImpression> => {
    const r = await apiClient.get(`/operations/textprint/commandes/${id}`);
    return unwrap<CommandeImpression>(r);
  },
  create: async (payload: CreateCommandePayload): Promise<CommandeImpression> => {
    const r = await apiClient.post('/operations/textprint/commandes', payload);
    return unwrap<CommandeImpression>(r);
  },
  update: async (id: string, payload: UpdateCommandePayload): Promise<CommandeImpression> => {
    const r = await apiClient.patch(`/operations/textprint/commandes/${id}`, payload);
    return unwrap<CommandeImpression>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/commandes/${id}`);
  },
};

// ── Ordres d'impression (OF) ───────────────────────────────────────────────

export interface OrdreListParams {
  statut?:     StatutOFImpression;
  priorite?:   PrioriteOF;
  machineId?:  string;
  commandeId?: string;
  search?:     string;
  page?:       number;
  limit?:      number;
}

export interface CreateOrdrePayload {
  reference?:             string;
  commandeId?:            string;
  machineId?:             string;
  operateurId?:           string;
  designation:            string;
  quantite:               number;
  dateCibleFin?:          string;
  priorite?:              PrioriteOF;
  statut?:                StatutOFImpression;
  tempsEstimeMin?:        number;
  soustraitant?:          string;
  soustraitantCoutCents?: number;
  notes?:                 string;
}
export interface UpdateOrdrePayload {
  commandeId?:            string;
  machineId?:             string;
  operateurId?:           string;
  designation?:           string;
  quantite?:              number;
  dateCibleFin?:          string;
  dateFinReelle?:         string;
  priorite?:              PrioriteOF;
  statut?:                StatutOFImpression;
  tempsEstimeMin?:        number;
  soustraitant?:          string;
  soustraitantCoutCents?: number;
  notes?:                 string;
}

export const textprintOrdresApi = {
  list: async (params: OrdreListParams = {}): Promise<Paginated<OrdreImpression>> => {
    const r = await apiClient.get('/operations/textprint/ordres', { params });
    return unwrapPaginated<OrdreImpression>(r);
  },
  get: async (id: string): Promise<OrdreImpression> => {
    const r = await apiClient.get(`/operations/textprint/ordres/${id}`);
    return unwrap<OrdreImpression>(r);
  },
  create: async (payload: CreateOrdrePayload): Promise<OrdreImpression> => {
    const r = await apiClient.post('/operations/textprint/ordres', payload);
    return unwrap<OrdreImpression>(r);
  },
  update: async (id: string, payload: UpdateOrdrePayload): Promise<OrdreImpression> => {
    const r = await apiClient.patch(`/operations/textprint/ordres/${id}`, payload);
    return unwrap<OrdreImpression>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/ordres/${id}`);
  },
};

// ── Consommables ───────────────────────────────────────────────────────────

export interface ConsommableListParams {
  typeConsommable?: TypeConsommable;
  machineRefId?:    string;
  sousAlerte?:      boolean;
  search?:          string;
  page?:            number;
  limit?:           number;
}

export interface CreateConsommablePayload {
  code:              string;
  designation:       string;
  typeConsommable:   TypeConsommable;
  unite:             UniteConsommable;
  stockActuel?:      number;
  stockMinimum?:     number;
  prixUnitaireCents?: number;
  fournisseur?:      string;
  delaiReappro?:     number;
  machineRefId?:     string;
  notes?:            string;
}
export type UpdateConsommablePayload = Partial<CreateConsommablePayload>;

export interface AjustementStockPayload {
  delta:  number;
  motif?: string;
}

export const textprintConsommablesApi = {
  list: async (params: ConsommableListParams = {}): Promise<Paginated<ConsommableImpression>> => {
    const r = await apiClient.get('/operations/textprint/consommables', { params });
    return unwrapPaginated<ConsommableImpression>(r);
  },
  get: async (id: string): Promise<ConsommableImpression> => {
    const r = await apiClient.get(`/operations/textprint/consommables/${id}`);
    return unwrap<ConsommableImpression>(r);
  },
  create: async (payload: CreateConsommablePayload): Promise<ConsommableImpression> => {
    const r = await apiClient.post('/operations/textprint/consommables', payload);
    return unwrap<ConsommableImpression>(r);
  },
  update: async (id: string, payload: UpdateConsommablePayload): Promise<ConsommableImpression> => {
    const r = await apiClient.patch(`/operations/textprint/consommables/${id}`, payload);
    return unwrap<ConsommableImpression>(r);
  },
  ajusterStock: async (id: string, payload: AjustementStockPayload): Promise<ConsommableImpression> => {
    const r = await apiClient.post(`/operations/textprint/consommables/${id}/ajuster-stock`, payload);
    return unwrap<ConsommableImpression>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/consommables/${id}`);
  },
};
